import { faBasketShoppingMagnifyingGlass } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import {
  faBurgerGlass, faCarBus, faCircleUser, faCircleUserCircleUser, faClothesHanger, faHandHoldingSeedling,
  faHouseBuilding, faMoneyBillTrendUp, faPeopleGroup, faReceipt, faStore, faSyringe, faUserPolice,
} from '@fortawesome/pro-solid-svg-icons';
import { type FaIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { UnreachableCaseException } from '~/_shared/exceptions/unreachableCaseException';
import type { TranslationFnc } from '~/_shared/utils/hooks';
import { type AgsReportCountry } from '~/exportReport/exportReport.constants';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import {
  type SubscriptionAddonCode, SubscriptionAddonCodes,
} from '~/subscription/addons/addons.types';
import type { Translations } from '~/translations/translations.type';
import ancestry_normal from '../template/converted/ancestry_normal.rt.yml';
import bdp from '../template/converted/bdp.rt.yml';
import can_crm from '../template/converted/can_crm.rt.yml';
import can_cxp1 from '../template/converted/can_cxp1.rt.yml';
import can_cxp3 from '../template/converted/can_cxp3.rt.yml';
import can_cxs from '../template/converted/can_cxs.rt.yml';
import can_cxt from '../template/converted/can_cxt.rt.yml';
import cex from '../template/converted/cex.rt.yml';
import crm from '../template/converted/crm.rt.yml';
import cxa from '../template/converted/cxa.rt.yml';
import cxf from '../template/converted/cxf.rt.yml';
import cxs from '../template/converted/cxs.rt.yml';
import cxt from '../template/converted/cxt.rt.yml';
import env from '../template/converted/env.rt.yml';
import gp5 from '../template/converted/gp5.rt.yml';
import hp from '../template/converted/hp.rt.yml';
import income from '../template/converted/income.rt.yml';
import mex_mho from '../template/converted/mex_mho.rt.yml';
import mex_mp1 from '../template/converted/mex_mp1.rt.yml';
import xp1 from '../template/converted/xp1.rt.yml';
import xp5 from '../template/converted/xp5.rt.yml';

export type TemplateSelectorItem = {
  id: string;
  template: ReportTemplate;
  requiredSubscriptionAddon: ReadonlyArray<SubscriptionAddonCode>;
  icon: FaIcon;
  title: keyof Translations;
  description: keyof Translations;
};

const usaReports: TemplateSelectorItem[] = [{
  id: 'key-demographics',
  template: xp1,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faCircleUser,
  title: 'Key Demographics',
  description: 'Top-level demographic snapshot, current year only.',
}, {
  id: 'demographic-essentials',
  template: xp5,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faCircleUserCircleUser,
  title: 'Demographic Essentials',
  description: 'Full demographic breakdown including trends and projections.',
}, {
  id: 'health-profile',
  template: hp,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faSyringe,
  title: 'Health Profile',
  description: 'Demographics, vital statistics, and hospital data.',
}, {
  id: 'cxs',
  template: cxs,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faHouseBuilding,
  title: 'Consumer Expenditures: Housing',
  description: 'Household shelter and utility spending with housing inventory.',
}, {
  id: 'cex',
  template: cex,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faReceipt,
  title: 'exportReport.template.cexTitle',
  description: 'exportReport.template.cexDescription',
}, {
  id: 'income',
  template: income,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faMoneyBillTrendUp,
  title: 'exportReport.template.incomeTitle',
  description: 'exportReport.template.incomeDescription',
}, {
  id: 'ancestry_normal',
  template: ancestry_normal,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_BASE, SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faPeopleGroup,
  title: 'exportReport.template.ancestryTitle',
  description: 'exportReport.template.ancestryDescription',
}, {
  id: 'crime-risk',
  template: crm,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faUserPolice,
  title: 'Crime Risk',
  description: 'Personal and property crime risk indices and grades.',
}, {
  id: 'environmental-risk',
  template: env,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faHandHoldingSeedling,
  title: 'Environmental risk and topography',
  description: 'Climate, terrain, air quality, and natural hazard risk grades.',
}, {
  id: 'business-details-profile',
  template: bdp,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faStore,
  title: 'Business Detail Profile',
  description: 'Establishment and employee counts by industry.',
}, {
  id: 'cxf',
  template: cxf,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faBurgerGlass,
  title: 'Consumer Expenditures: Food and Grocery',
  description: 'Household spending on groceries, dining out, and drinks.',
}, {
  id: 'cxa',
  template: cxa,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faClothesHanger,
  title: 'Consumer Expenditures: Apparel',
  description: 'Household clothing and apparel spending by category.',
}, {
  id: 'cxt',
  template: cxt,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faCarBus,
  title: 'Consumer Expenditures: Transportation',
  description: 'Household transportation spending and commute data.',
}, {
  id: 'gp5',
  template: gp5,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_US_PREMIUM],
  icon: faBasketShoppingMagnifyingGlass,
  title: 'Retail Gap Analysis',
  description: 'exportReport.templateSelector.gapDescription',
}];

const canadaReports: TemplateSelectorItem[] = [
  {
    id: 'key-demographics',
    template: can_cxp1,
    requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_CA_BASE, SubscriptionAddonCodes.AGS_CA_PREMIUM],
    icon: faCircleUser,
    title: 'Key Demographics',
    description: 'Top-level demographic snapshot, current year only.',
  },
  {
    id: 'demographic-essentials',
    template: can_cxp3,
    requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_CA_BASE, SubscriptionAddonCodes.AGS_CA_PREMIUM],
    icon: faCircleUserCircleUser,
    title: 'Demographic Essentials',
    description: 'Full demographic breakdown including trends and projections.',
  },
  {
    id: 'cxs',
    template: can_cxs,
    requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_CA_BASE, SubscriptionAddonCodes.AGS_CA_PREMIUM],
    icon: faHouseBuilding,
    title: 'Consumer Expenditures: Housing',
    description: 'Household shelter and utility spending with housing inventory.',
  },
  {
    id: 'crime-risk',
    template: can_crm,
    requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_CA_PREMIUM],
    icon: faUserPolice,
    title: 'Crime Risk',
    description: 'Personal and property crime risk indices and grades.',
  },
  {
    id: 'cxt',
    template: can_cxt,
    requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_CA_PREMIUM],
    icon: faCarBus,
    title: 'Consumer Expenditures: Transportation',
    description: 'Household transportation spending and commute data.',
  },
];

const mexicoReports: TemplateSelectorItem[] = [{
  id: 'mex-key-demographics',
  template: mex_mp1,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_MX_BASE],
  icon: faCircleUser,
  title: 'Key Demographics',
  description: 'Top-level demographic snapshot, current year only.',
}, {
  id: 'mex-cxs',
  template: mex_mho,
  requiredSubscriptionAddon: [SubscriptionAddonCodes.AGS_MX_BASE],
  icon: faHouseBuilding,
  title: 'Consumer Expenditures: Housing',
  description: 'Household shelter and utility spending with housing inventory.',
}];

export const getAreaReportItems = (area: ExportReportTemplateSelectorArea) => {
  switch (area) {
    case ExportReportTemplateSelectorArea.USA:
      return usaReports;
    case ExportReportTemplateSelectorArea.Canada:
      return canadaReports;
    case ExportReportTemplateSelectorArea.Mexico:
      return mexicoReports;
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};

export const getReportAreaTranslation = (area: ExportReportTemplateSelectorArea, t: TranslationFnc) => {
  switch (area) {
    case ExportReportTemplateSelectorArea.Canada:
      return t('Canada');
    case ExportReportTemplateSelectorArea.USA:
      return t('USA');
    case ExportReportTemplateSelectorArea.Mexico:
      return t('Mexico');
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};

export const getAgsReportCountryForArea = (area: ExportReportTemplateSelectorArea): AgsReportCountry => {
  switch (area) {
    case ExportReportTemplateSelectorArea.Mexico:
      return 'mex';
    case ExportReportTemplateSelectorArea.Canada:
      return 'can';
    case ExportReportTemplateSelectorArea.USA:
      return 'usa';
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};
